import type { IntlShape } from 'react-intl';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { NATIVE_EMBED_EXTENSION_KEY } from '@atlaskit/editor-common/extensions';
import type { Command, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { findDomRefAtPos, type ContentNodeWithPos } from '@atlaskit/editor-prosemirror/utils';
import {
	EDITOR_TOOLBAR_HANDLER_KEYS,
	type AlignmentValue,
	type EditorToolbarHandlerContext,
	type EditorToolbarHandlers,
	type NativeEmbedAppearance,
	type NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type {
	EditorPluginNativeEmbedsPlugin,
	EditorToolbarHandlersWithDefaults,
} from '../../nativeEmbedsPluginType';
import {
	createCopyLinkCommand,
	createDeleteCommand,
	createOpenInNewWindowCommand,
	createToggleAlwaysShowTitle,
	createUpdateAlignmentCommand,
} from '../commands';
import { setNativeEmbedAppearance } from '../commands/appearance';
import { showUrlToolbar } from '../commands/url';

const createHandlerCommand =
	(handler?: () => void): Command =>
	() => {
		handler?.();
		return true;
	};

function wrapManifestHandler(
	handler: (p: NativeEmbedParameterValues, context?: EditorToolbarHandlerContext) => void,
	parameters: NativeEmbedParameterValues,
	handlerContext: EditorToolbarHandlerContext,
): () => Command {
	return () => createHandlerCommand(() => handler(parameters, handlerContext));
}

function wrapManifestHandlerWithContext<C>(
	handler: (
		p: NativeEmbedParameterValues,
		context: C,
		handlerContext?: EditorToolbarHandlerContext,
	) => void,
	parameters: NativeEmbedParameterValues,
	handlerContext: EditorToolbarHandlerContext,
): (context: C) => Command {
	return (context) => createHandlerCommand(() => handler(parameters, context, handlerContext));
}

/**
 * Creates a Command that copies the native-embed node to the clipboard.
 * Delegates to the floating-toolbar plugin's `copyNode` command when available,
 * falling back to `createCopyLinkCommand` (copies URL string) when the
 * floating-toolbar plugin is not present.
 */
/**
 * Extracts the hostname from a URL string for use as a fallback display name
 * in the Rovo chat context banner.
 */
function getHostnameFromUrl(url: string): string {
	try {
		return new URL(url).hostname;
	} catch {
		return '';
	}
}

function isMauiNativeEmbed(extensionKey: unknown): boolean {
	return extensionKey === `${NATIVE_EMBED_EXTENSION_KEY}:maui`;
}

function createCopyNodeCommand(
	selectedNativeEmbed: ContentNodeWithPos,
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
): Command {
	const copyNodeCommand = api?.floatingToolbar?.commands?.copyNode(
		selectedNativeEmbed.node.type,
		INPUT_METHOD.FLOATING_TB,
	);

	if (copyNodeCommand) {
		return () => {
			api?.core?.actions.execute(copyNodeCommand);
			return true;
		};
	}

	// Fallback: copy the URL string when floating-toolbar plugin is unavailable
	return createCopyLinkCommand(selectedNativeEmbed.node);
}

/**
 * Creates default EditorToolbarHandlers with actual functionality.
 * Injects editorAPI for commands that need it (e.g. alignment, open-in-new-window).
 */
export function createDefaultEditorToolbarHandlers(
	selectedNativeEmbed: ContentNodeWithPos,
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
	intl: IntlShape,
	schema: Schema,
	parameters: NativeEmbedParameterValues,
): EditorToolbarHandlersWithDefaults {
	const node = selectedNativeEmbed.node;
	const askRovoTarget =
		isMauiNativeEmbed(node.attrs?.extensionKey) &&
		expValEquals('cc-maui-experiment', 'isEnabled', true)
			? 'remix-app'
			: 'default';

	const embedUrl = parameters.url ?? '';
	const embedName = getHostnameFromUrl(embedUrl);

	const askRovoButton = api?.aiExperience?.actions?.getAskRovoFloatingToolbarButton?.({
		intl,
		triggeredFor: schema.nodes.extension,
		target: askRovoTarget,
		openInRovoChat: true,
		rovoChatAIFeatureContext: embedName
			? {
					projectContext: {
						projectId: embedUrl,
						projectName: embedName,
						projectUrl: embedUrl,
					},
			  }
			: undefined,
	});

	const summariseButton = parameters.url
		? api?.aiExperience?.actions?.getSummariseSmartlinkFloatingToolbarButton?.({
				intl,
				smartlinkUrl: parameters.url,
				triggeredFor: schema.nodes.extension,
		  })
		: undefined;

	return {
		onAppearanceClick: (appearance: NativeEmbedAppearance) =>
			setNativeEmbedAppearance(selectedNativeEmbed, appearance, api?.analytics?.actions),
		onAlignmentClick: (alignment: AlignmentValue) =>
			createUpdateAlignmentCommand(api, alignment, node),
		onCopyClick: () => createCopyNodeCommand(selectedNativeEmbed, api),
		onCopyLinkClick: () => createCopyLinkCommand(node),
		onDeleteClick: () => createDeleteCommand(selectedNativeEmbed),
		onShowTitleClick: () => createHandlerCommand(createToggleAlwaysShowTitle(api, node)),
		onExpandClick: () => (_state, _dispatch, view) => {
			if (view) {
				try {
					const domRef = findDomRefAtPos(selectedNativeEmbed.pos, view.domAtPos.bind(view));
					if (domRef instanceof HTMLElement) {
						const extensionContainer = domRef.classList.contains('extension-container')
							? domRef
							: (domRef.querySelector('.extension-container') as HTMLElement | null) ?? domRef;
						extensionContainer.dispatchEvent(
							new CustomEvent('native-embed-open-fullscreen', { bubbles: true }),
						);
					}
				} catch {
					// ignore
				}
			}
			return true;
		},
		onOpenInNewWindowClick: () => createOpenInNewWindowCommand(node),
		onEditUrlClick: () => showUrlToolbar,
		onEditClick: () => showUrlToolbar,
		askRovoButton,
		onAskRovoClick: askRovoButton ? () => askRovoButton.onClick : undefined,
		getRemixVariantButton: api?.malleableUi?.actions?.getRemixVariantFloatingToolbarButton,
		onSummariseClick: () => summariseButton?.onClick ?? createHandlerCommand(() => {}),
		onRefreshClick: () => createHandlerCommand(() => {}),
		onChangeBorderClick: () => createHandlerCommand(() => {}),
		onSetEmbedTypeClick: () => createHandlerCommand(() => {}),
	};
}

/**
 * Builds an EditorToolbarHandlersWithDefaults object by merging manifest handlers with default handlers.
 * For each handler key, uses the manifest handler if defined, otherwise the default action.
 */
export function buildToolbarHandlers(
	selectedNativeEmbed: ContentNodeWithPos,
	manifestHandlers: EditorToolbarHandlers | undefined,
	api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
	parameters: NativeEmbedParameterValues,
	intl: IntlShape,
	schema: Schema,
): EditorToolbarHandlersWithDefaults {
	const defaultHandlers = createDefaultEditorToolbarHandlers(
		selectedNativeEmbed,
		api,
		intl,
		schema,
		parameters,
	);
	const result: EditorToolbarHandlersWithDefaults = { ...defaultHandlers };
	const selectedNodeLocalId =
		typeof selectedNativeEmbed.node.attrs?.localId === 'string'
			? selectedNativeEmbed.node.attrs.localId
			: undefined;
	const handlerContext: EditorToolbarHandlerContext = {
		editorApi: api,
		selectedNodeLocalId,
	};

	for (const key of EDITOR_TOOLBAR_HANDLER_KEYS) {
		const manifestHandler = manifestHandlers?.[key];
		if (!manifestHandler) continue;

		switch (key) {
			case 'onAlignmentClick':
				result.onAlignmentClick = wrapManifestHandlerWithContext<AlignmentValue>(
					manifestHandler as NonNullable<EditorToolbarHandlers['onAlignmentClick']>,
					parameters,
					handlerContext,
				);
				break;
			case 'onAppearanceClick':
				result.onAppearanceClick = wrapManifestHandlerWithContext<NativeEmbedAppearance>(
					manifestHandler as NonNullable<EditorToolbarHandlers['onAppearanceClick']>,
					parameters,
					handlerContext,
				);
				break;
			case 'onAskRovoClick':
				if (fg('platform_editor_maui_jira_updates')) {
					result[key] =
						result[key] !== undefined
							? wrapManifestHandler(
									manifestHandler as (
										p: NativeEmbedParameterValues,
										context?: EditorToolbarHandlerContext,
									) => void,
									parameters,
									handlerContext,
							  )
							: undefined;
				} else {
					result[key] = wrapManifestHandler(
						manifestHandler as (
							p: NativeEmbedParameterValues,
							context?: EditorToolbarHandlerContext,
						) => void,
						parameters,
						handlerContext,
					);
				}
				break;
			default:
				result[key] = wrapManifestHandler(
					manifestHandler as (
						p: NativeEmbedParameterValues,
						context?: EditorToolbarHandlerContext,
					) => void,
					parameters,
					handlerContext,
				);
		}
	}

	return result;
}
